import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';

import {
  FaChevronRight, FaNewspaper, FaFileAlt, FaComments,
  FaDollarSign, FaChartBar, FaBriefcase, FaAward
} from 'react-icons/fa';

const buildFaqSchema = (pairs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: pairs.map(([q, a]) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
});

const YEAR = new Date().getFullYear();

const NAV_LINKS = [
  { slug: 'blog',               label: 'Career Advice Blog',  icon: <FaNewspaper /> },
  { slug: 'resume-templates',   label: 'Resume Templates',    icon: <FaFileAlt /> },
  { slug: 'interview-tips',     label: 'Interview Tips',      icon: <FaComments /> },
  { slug: 'salary-guide',       label: 'Salary Guide',        icon: <FaDollarSign /> },
  { slug: 'industry-reports',   label: 'Industry Reports',    icon: <FaChartBar /> },
  { slug: 'get-certified',      label: 'Get Certified',        icon: <FaAward /> },
];

// ─── Page configs ────────────────────────────────────────────────────────────

const PAGES = {
  blog: {
    title: `Jamaica Career Advice Blog ${YEAR} | HireMeJA`,
    description: 'Career tips, job search advice, and hiring insights for Jamaican professionals. Read the HireMeJA blog for expert guidance on landing your next job in Jamaica.',
    heading: 'Career Advice Blog',
    intro: `Stay ahead in Jamaica's job market with expert career advice, job search strategies, and industry insights written for Jamaican professionals. New articles published weekly.`,
    content: <BlogContent />,
  },
  'resume-templates': {
    title: `Free Resume Templates for Jamaica ${YEAR} | HireMeJA`,
    description: 'Download free resume templates tailored for the Jamaican job market. Learn how to write a CV that gets noticed by Jamaican employers.',
    heading: 'Resume Templates & Writing Guide',
    intro: `Your resume is your first impression. Use these templates and tips to craft a CV that stands out to Jamaican employers — whether you're applying for your first job or moving up the career ladder.`,
    content: <ResumeContent />,
  },
  'interview-tips': {
    title: `Job Interview Tips Jamaica ${YEAR} | HireMeJA Career Guide`,
    description: 'Prepare for your next job interview in Jamaica. Tips on what to wear, what to say, and how to impress Jamaican employers and land the job.',
    heading: 'Job Interview Tips for Jamaica',
    intro: `Interviews can be nerve-wracking, but preparation makes all the difference. These tips are tailored to the Jamaican workplace culture and will help you walk in confident and walk out with an offer.`,
    content: <InterviewContent />,
    faqSchema: buildFaqSchema([
      ['What should I wear to a job interview in Jamaica?', `Dress professionally for job interviews in Jamaica. Business formal is safest — a suit or dress shirt and trousers for men, and a professional dress, blouse, or trouser suit for women. Clean, pressed clothing and neat grooming show respect for the opportunity and signal that you take the role seriously.`],
      ['How early should I arrive for a job interview in Jamaica?', `Arrive 10 to 15 minutes early for a job interview in Jamaica. Punctuality is taken seriously by Jamaican employers. Factor in Kingston or Montego Bay traffic and allow extra time for parking, especially in commercial areas.`],
      ['What are common job interview questions in Jamaica?', `Common interview questions in Jamaica include: "Tell me about yourself," "What are your strengths and weaknesses?", "Where do you see yourself in five years?", "Why do you want to work here?", and "What salary are you expecting?" Prepare specific answers for each using examples from your experience.`],
      ['Should I send a thank you email after a job interview in Jamaica?', `Yes. Sending a brief thank you email within 24 hours of your interview is good professional practice in Jamaica. Reiterate your interest in the role, mention something specific from the conversation, and keep it concise. Most candidates don't do this, so it helps you stand out.`],
      ['How do I prepare for a job interview in Jamaica?', `Research the company thoroughly before your interview. Know their products or services, their customers, and any recent news. Prepare answers to common interview questions, bring copies of your CV and certificates, dress professionally, and arrive 10–15 minutes early.`],
      ['What documents should I bring to a job interview in Jamaica?', `Bring multiple printed copies of your CV, your original certificates and qualifications, a valid ID, and any portfolio or work samples relevant to the role. Some Jamaican employers also ask for a police record, so check the job listing in advance.`],
    ]),
  },
  'salary-guide': {
    title: `Jamaica Salary Guide ${YEAR} | HireMeJA`,
    description: `Explore average salaries across Jamaica's top industries in ${YEAR}. Use HireMeJA's salary guide to negotiate better pay and understand your market value.`,
    heading: `Jamaica Salary Guide ${YEAR}`,
    intro: `Understanding what you're worth is the first step to negotiating better pay. This guide covers average salary ranges across Jamaica's key industries, based on current job market data.`,
    content: <SalaryContent />,
    faqSchema: buildFaqSchema([
      ['What is the average salary in Jamaica?', `The average monthly salary in Jamaica varies widely by industry and experience. Entry-level roles typically pay JMD 35,000–80,000 per month, mid-level roles JMD 80,000–200,000, and senior professionals JMD 200,000–500,000+ depending on the sector.`],
      ['What is the average IT salary in Jamaica?', `IT professionals in Jamaica earn JMD 80,000–120,000 per month at entry level, JMD 150,000–250,000 at mid level, and JMD 300,000–500,000+ at senior level. Demand for developers and data analysts is high and growing.`],
      ['What is the average nurse salary in Jamaica?', `Nurses in Jamaica typically earn JMD 55,000–90,000 per month at entry level and JMD 100,000–180,000 at mid level. Senior nurses and nurse managers can earn JMD 200,000–400,000 per month.`],
      ['What is the minimum wage in Jamaica?', `Jamaica's national minimum wage is set by the government and applies to most workers. Employers are legally required to pay at least the minimum wage. Check the Ministry of Labour website for the current rate as it is reviewed periodically.`],
      ['What is the average BPO salary in Jamaica?', `BPO and customer service professionals in Jamaica earn JMD 50,000–80,000 per month at entry level, JMD 90,000–150,000 at mid level, and JMD 170,000–280,000 at senior or managerial level.`],
      ['What is the average salary for a banker in Jamaica?', `Banking and finance professionals in Jamaica earn JMD 60,000–100,000 at entry level, JMD 120,000–200,000 at mid level, and JMD 250,000–450,000 at senior level.`],
      ['How do I negotiate a salary in Jamaica?', `Research the market rate for your role and industry using resources like the HireMeJA salary guide. Know your worth before the interview, and be prepared to justify your ask with your experience, qualifications, and the value you bring to the company.`],
    ]),
  },
  'industry-reports': {
    title: `Jamaica Industry Employment Reports ${YEAR} | HireMeJA`,
    description: `In-depth employment reports for Jamaica's key industries in ${YEAR}. Understand hiring trends, demand, and growth sectors across the Jamaican job market.`,
    heading: `Jamaica Industry Employment Reports`,
    intro: `Get a data-driven look at Jamaica's employment landscape. These industry reports highlight where jobs are growing, which skills are in demand, and what employers across Jamaica are looking for in ${YEAR}.`,
    content: <ReportsContent />,
  },
  'get-certified': {
    title: `Get Certified in Jamaica ${YEAR} | HEART Trust & Professional Certifications | HireMeJA`,
    description: `Explore HEART/NSTA Trust certification programmes, Ministry of Labour initiatives, and professional certifications available in Jamaica. Get certified and advance your career on HireMeJA.`,
    heading: 'Get Certified in Jamaica',
    intro: `Certification opens doors. Whether you're pursuing a HEART/NSTA Trust qualification, a government-backed programme, or an internationally recognised professional credential, we've mapped out the best paths to upskill and get hired faster in Jamaica.`,
    content: <GetCertifiedContent />,
    faqSchema: buildFaqSchema([
      ['What is HEART Trust Jamaica?', `HEART/NSTA Trust (Human Employment and Resource Training / National Service Training Agency) is Jamaica's national training and certification agency. It offers free and subsidised vocational and technical training across dozens of industries, from IT to construction to hospitality, leading to National Vocational Qualifications of Jamaica (NVQJ).`],
      ['How do I get a HEART/NSTA certification in Jamaica?', `To get HEART certified, apply directly through the HEART/NSTA Trust website or visit a HEART Academy near you. Choose a programme in your area of interest, complete the required training hours and assessments, and receive a National Vocational Qualification of Jamaica (NVQJ) certificate upon passing. Intake periods typically run in January and September.`],
      ['Are HEART Trust courses free in Jamaica?', `Many HEART/NSTA Trust programmes are fully funded by the Jamaican government and are free for eligible applicants. Some specialised or advanced programmes may have a nominal fee. Check the HEART/NSTA Trust website for current programme costs and eligibility requirements.`],
      ['What professional certifications are most valued by Jamaican employers?', `Jamaican employers highly value internationally recognised certifications. In IT: AWS, CompTIA, CISSP, and ITIL 4. In finance: ACCA, CPA, and CIMA. In project management: PMP and PRINCE2. In HR: SHRM-CP and CIPD. These credentials signal globally competitive skills and typically command higher salaries in the Jamaican job market.`],
      ['How do I apply for Ministry of Labour job fairs in Jamaica?', `Ministry of Labour job fairs are typically advertised on the Ministry's official website and social media channels. Registration is usually free and done online or at regional labour offices. Bring your CV and any certificates. Employers from multiple industries attend and conduct on-the-spot interviews.`],
      ['Does getting certified help me get a job in Jamaica?', `Yes. Certification significantly improves your employability in Jamaica. Employers use certifications as a screening signal — especially for technical and trade roles. HEART/NSTA Trust certifications are widely recognised by Jamaican employers, while international professional certifications like ACCA or AWS can unlock higher-paying roles and faster career progression.`],
    ]),
  },
};

// ─── Page content sections ───────────────────────────────────────────────────

function BlogContent() {
  const articles = [
    {
      title: `Why a Premium Job Post in Jamaica is Your Best Hiring Move in ${YEAR}`,
      category: 'For Employers',
      excerpt: 'Stop wasting time on irrelevant resumes. Learn how featured job listings cut through the noise and connect you with top Jamaican talent faster.',
      date: `May ${YEAR}`,
      slug: 'premium-job-post-jamaica',
    },
    {
      title: 'How to Write a CV That Gets Noticed by Jamaican Employers',
      category: 'Career Advice',
      excerpt: 'A strong CV is your first impression. We break down what Jamaican hiring managers look for and how to structure your resume for maximum impact.',
      date: `May ${YEAR}`,
      slug: 'how-to-write-a-cv-jamaica',
    },
    {
      title: '5 AI Mistakes Jamaican Employers Make When Hiring (And How to Fix Them)',
      category: 'For Employers',
      excerpt: 'Most businesses are using AI in hiring the wrong way. From generic job descriptions to automated decisions — here are the five most common mistakes and how to fix them.',
      date: `May ${YEAR}`,
      slug: 'ai-mistakes-jamaican-employers-hiring',
    },
    {
      title: 'The Top 10 Growing Industries in Jamaica Right Now',
      category: 'Industry Insights',
      excerpt: 'From BPO to renewable energy, discover which sectors are creating the most career opportunities in Jamaica and how to position yourself for success.',
      date: `April ${YEAR}`,
      slug: 'growing-industries-jamaica',
    },
    {
      title: '7 Salary Negotiation Tips for Jamaican Job Seekers',
      category: 'Career Advice',
      excerpt: 'Negotiating your salary doesn\'t have to be uncomfortable. Here\'s how to research market rates and make a confident case for better pay.',
      date: `March ${YEAR}`,
      slug: 'salary-negotiation-tips-jamaica',
    },
    {
      title: 'Remote Work in Jamaica: Opportunities, Challenges & How to Find Remote Jobs',
      category: 'Job Search Tips',
      excerpt: 'Remote work is growing in Jamaica. Learn which companies are hiring remotely, how to stand out as a remote candidate, and what tools you need.',
      date: `March ${YEAR}`,
      slug: 'remote-work-jamaica',
    },
    {
      title: 'Entry Level Jobs in Jamaica: How to Land Your First Role',
      category: 'Job Search Tips',
      excerpt: 'No experience? No problem. This guide walks you through how to find, apply for, and land entry-level jobs across Jamaica\'s major industries.',
      date: `February ${YEAR}`,
      slug: 'entry-level-jobs-jamaica',
    },
  ];

  const categoryColors = {
    'For Employers': 'bg-blue-100 text-blue-800',
    'Career Advice': 'bg-green-100 text-green-800',
    'Industry Insights': 'bg-purple-100 text-purple-800',
    'Job Search Tips': 'bg-yellow-100 text-yellow-800',
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      {articles.map((article, i) => (
        <div key={i} className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${categoryColors[article.category] || 'bg-gray-100 text-gray-700'}`}>
            {article.category}
          </span>
          <h3 className="text-sm font-semibold text-blue-950 mt-2 leading-snug">{article.title}</h3>
          <p className="text-xs text-gray-500 mt-1">{article.date}</p>
          <p className="text-sm text-gray-600 mt-2 leading-relaxed">{article.excerpt}</p>
          {article.slug ? (
            <Link
              to={`/career-resources/blog/${article.slug}`}
              className="text-xs text-blue-950 font-medium mt-3 flex items-center gap-1 hover:text-[#cddd3a] transition-colors"
            >
              Read article <FaChevronRight size={9} />
            </Link>
          ) : (
            <p className="text-xs text-blue-950 font-medium mt-3 flex items-center gap-1">
              Full article coming soon <FaChevronRight size={9} />
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

function CvExamples() {
  return (
    <div className="mb-8">
      <h3 className="text-lg font-semibold text-blue-950 mb-1">Good CV vs. Bad CV — Real Examples</h3>
      <p className="text-sm text-gray-600 mb-5 leading-relaxed">
        See exactly what Jamaican employers mean when they say a CV "stands out." Both examples
        are for the same candidate. The difference is entirely in structure, language, and presentation.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {/* Poor CV */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 bg-red-50 text-red-700 border border-red-200 text-xs font-bold px-3 py-1 rounded-full">
              <span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> Poor CV
            </span>
          </div>
          <ul className="text-xs text-gray-500 space-y-1 mb-3">
            {[
              'No professional summary',
              'Duties listed, not achievements',
              'Inconsistent formatting',
              'Missing qualification grades',
            ].map((f, i) => (
              <li key={i} className="flex items-center gap-1.5">
                <span className="text-red-400 font-bold">✗</span> {f}
              </li>
            ))}
          </ul>
          <div className="border-2 border-red-200 rounded-lg bg-white p-4 text-[10px] leading-snug">
            <div className="text-center mb-3">
              <div className="text-sm font-bold text-gray-800">JORDAN CAMPBELL</div>
              <div className="text-gray-500">Kingston, Jamaica &nbsp;|&nbsp; jordancampbell@yahoo.com</div>
            </div>
            <div className="mb-3">
              <div className="text-[9px] font-bold uppercase tracking-widest text-gray-400 border-b border-gray-200 mb-1">Work Experience</div>
              <div className="font-semibold text-gray-700">Customer Service Rep - BIC Insurance</div>
              <div className="text-gray-400 italic mb-1">2019 – 2023</div>
              <div className="text-gray-600">Responsible for handling customer calls. Did filing and administrative work. Helped customers with their queries and complaints.</div>
            </div>
            <div className="mb-3">
              <div className="font-semibold text-gray-700">Cashier - SuperValu</div>
              <div className="text-gray-400 italic mb-1">2017 – 2019</div>
              <div className="text-gray-600">Worked the cash register. Assisted customers. Responsible for customer service duties.</div>
            </div>
            <div className="mb-3">
              <div className="text-[9px] font-bold uppercase tracking-widest text-gray-400 border-b border-gray-200 mb-1">Education</div>
              <div className="font-semibold text-gray-700">Kingston Technical High School</div>
              <div className="text-gray-600">Subjects: English, Maths, Accounts, IT</div>
              <div className="text-red-400 italic text-[9px] mt-0.5">← No grades listed</div>
            </div>
            <div>
              <div className="text-[9px] font-bold uppercase tracking-widest text-gray-400 border-b border-gray-200 mb-1">Skills</div>
              <div className="text-gray-600">Computer skills, Good communication, Team player, Hard worker, Friendly</div>
            </div>
          </div>
        </div>

        {/* Strong CV */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 border border-green-200 text-xs font-bold px-3 py-1 rounded-full">
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Strong CV
            </span>
          </div>
          <ul className="text-xs text-gray-500 space-y-1 mb-3">
            {[
              'Tailored professional summary',
              'Achievement-based bullets with numbers',
              'Clean, consistent layout',
              'Qualifications listed with grades',
            ].map((f, i) => (
              <li key={i} className="flex items-center gap-1.5">
                <span className="text-green-500 font-bold">✓</span> {f}
              </li>
            ))}
          </ul>
          <div className="border-2 border-green-200 rounded-lg bg-white p-4 text-[10px] leading-snug">
            <div className="text-center mb-3 pb-2 border-b-2 border-blue-950">
              <div className="text-sm font-bold text-blue-950">JORDAN CAMPBELL</div>
              <div className="text-gray-500">Kingston, Jamaica &nbsp;|&nbsp; jordan.campbell@gmail.com &nbsp;|&nbsp; (876) 555-0192</div>
            </div>
            <div className="mb-3">
              <div className="text-[9px] font-bold uppercase tracking-widest text-blue-950 border-b border-blue-950 mb-1">Professional Summary</div>
              <div className="text-gray-600">Customer-focused professional with 4+ years in financial services and retail. Maintains a 94% first-call resolution rate. Seeking a BPO team lead role to apply strong communication and problem-solving skills.</div>
            </div>
            <div className="mb-3">
              <div className="text-[9px] font-bold uppercase tracking-widest text-blue-950 border-b border-blue-950 mb-1">Work Experience</div>
              <div className="font-semibold text-gray-800">Customer Service Representative — BIC Insurance</div>
              <div className="text-gray-400 mb-1">Jan 2019 – Dec 2023 · Kingston</div>
              <ul className="text-gray-600 space-y-0.5 pl-2">
                <li>• Managed 80+ daily calls with 94% first-call resolution rate</li>
                <li>• Cut average handling time by 12% through workflow improvements</li>
                <li>• Onboarded and mentored 6 new agents</li>
              </ul>
            </div>
            <div className="mb-3">
              <div className="font-semibold text-gray-800">Cashier — SuperValu Supermarket</div>
              <div className="text-gray-400 mb-1">Jun 2017 – Dec 2018 · Kingston</div>
              <ul className="text-gray-600 space-y-0.5 pl-2">
                <li>• Processed $2M+ weekly with zero cash discrepancies over 18 months</li>
                <li>• Employee of the Month ×2 for customer satisfaction scores</li>
              </ul>
            </div>
            <div className="mb-3">
              <div className="text-[9px] font-bold uppercase tracking-widest text-blue-950 border-b border-blue-950 mb-1">Education</div>
              <div className="font-semibold text-gray-800">Kingston Technical High School — CSEC (2017)</div>
              <div className="text-gray-600">English Language: Gr. 1 &nbsp;|&nbsp; Mathematics: Gr. 2 &nbsp;|&nbsp; Accounts: Gr. 2 &nbsp;|&nbsp; IT: Gr. 1</div>
            </div>
            <div>
              <div className="text-[9px] font-bold uppercase tracking-widest text-blue-950 border-b border-blue-950 mb-1">Skills</div>
              <div className="text-gray-600"><span className="font-semibold">Technical:</span> Microsoft Office, POS Systems, Salesforce CRM</div>
              <div className="text-gray-600 mt-0.5"><span className="font-semibold">Soft Skills:</span> Active Listening, Conflict Resolution, Team Leadership</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ResumeContent() {
  const sections = [
    {
      heading: '1. Keep It to One or Two Pages',
      body: 'Jamaican employers typically prefer concise CVs. Unless you have 10+ years of experience, keep it to one page. Use clear headings and bullet points — don\'t bury your achievements in paragraphs.',
    },
    {
      heading: '2. Lead With a Strong Summary',
      body: 'Open with 2–3 sentences that tell the employer who you are, what you do, and what value you bring. Tailor this section to each job you apply for.',
    },
    {
      heading: '3. List Experience in Reverse Chronological Order',
      body: 'Your most recent job goes first. For each role, list your title, company, dates, and 3–5 bullet points of achievements — not just duties. Use numbers where possible ("Increased sales by 20%").',
    },
    {
      heading: '4. Include Jamaican Qualifications',
      body: 'List CXC/CSEC grades, CAPE, NVQ, HEART Trust/NTA certifications, and any university degrees. Jamaican employers recognize these and want to see them prominently.',
    },
    {
      heading: '5. References: "Available on Request"',
      body: 'You don\'t need to include full reference contact details on your CV. "References available upon request" is standard practice in Jamaica. Have at least two professional references ready.',
    },
    {
      heading: '6. Proofread Everything',
      body: 'Spelling errors and grammatical mistakes are an instant red flag. Have someone else read your CV before sending. Use tools like Grammarly as a starting point.',
    },
  ];

  return (
    <div className="space-y-5">
      <CvExamples />
      <h3 className="text-lg font-semibold text-blue-950">CV Writing Tips for the Jamaican Job Market</h3>
      <div className="space-y-4">
        {sections.map((s, i) => (
          <div key={i} className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-950">{s.heading}</h4>
            <p className="text-sm text-gray-600 mt-1 leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function InterviewContent() {
  const tips = [
    {
      heading: 'Research the Company First',
      body: 'Before your interview, learn what the company does, who their customers are, and any recent news about them. Mentioning something specific you admire shows genuine interest and sets you apart from candidates who didn\'t prepare.',
    },
    {
      heading: 'Dress Professionally',
      body: 'In Jamaica\'s professional culture, appearance matters. Dress one level above what the role requires — if the job is business casual, come in business formal. Clean, pressed clothing and neat grooming signal that you take the opportunity seriously.',
    },
    {
      heading: 'Arrive 10–15 Minutes Early',
      body: 'Punctuality is taken seriously by Jamaican employers. Arriving late — even by a few minutes — can end your chances before the interview starts. Factor in traffic and parking, especially in Kingston and Montego Bay.',
    },
    {
      heading: 'Prepare for Common Questions',
      body: 'Be ready for: "Tell me about yourself", "What are your strengths and weaknesses?", "Where do you see yourself in 5 years?", and "Why do you want to work here?". Practice your answers out loud — not just in your head.',
    },
    {
      heading: 'Have Questions Ready to Ask',
      body: 'When the interviewer asks "Do you have any questions?", always say yes. Ask about the team, growth opportunities, or day-to-day expectations. It shows you\'re thinking seriously about the role.',
    },
    {
      heading: 'Follow Up With a Thank You',
      body: 'Send a brief email within 24 hours thanking the interviewer for their time. Reiterate your interest in the role and mention one specific thing from the conversation. Most candidates don\'t do this — it makes you memorable.',
    },
  ];

  return (
    <div className="space-y-4">
      {tips.map((tip, i) => (
        <div key={i} className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-950 text-white text-xs font-bold flex items-center justify-center mt-0.5">
              {i + 1}
            </span>
            <div>
              <h3 className="text-sm font-semibold text-blue-950">{tip.heading}</h3>
              <p className="text-sm text-gray-600 mt-1 leading-relaxed">{tip.body}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SalaryContent() {
  const salaries = [
    { industry: 'Information Technology',      entry: 'JMD 80,000–120,000',  mid: 'JMD 150,000–250,000', senior: 'JMD 300,000–500,000+' },
    { industry: 'Banking & Finance',           entry: 'JMD 60,000–100,000',  mid: 'JMD 120,000–200,000', senior: 'JMD 250,000–450,000' },
    { industry: 'Healthcare',                  entry: 'JMD 55,000–90,000',   mid: 'JMD 100,000–180,000', senior: 'JMD 200,000–400,000' },
    { industry: 'Tourism & Hospitality',       entry: 'JMD 40,000–70,000',   mid: 'JMD 80,000–140,000',  senior: 'JMD 160,000–280,000' },
    { industry: 'BPO & Customer Service',      entry: 'JMD 50,000–80,000',   mid: 'JMD 90,000–150,000',  senior: 'JMD 170,000–280,000' },
    { industry: 'Education',                   entry: 'JMD 45,000–75,000',   mid: 'JMD 85,000–140,000',  senior: 'JMD 160,000–250,000' },
    { industry: 'Construction & Engineering',  entry: 'JMD 50,000–85,000',   mid: 'JMD 100,000–180,000', senior: 'JMD 200,000–380,000' },
    { industry: 'Retail & Sales',              entry: 'JMD 35,000–60,000',   mid: 'JMD 70,000–120,000',  senior: 'JMD 140,000–220,000' },
    { industry: 'Marketing & Communications', entry: 'JMD 55,000–90,000',   mid: 'JMD 100,000–170,000', senior: 'JMD 190,000–320,000' },
    { industry: 'Legal',                       entry: 'JMD 70,000–110,000',  mid: 'JMD 140,000–250,000', senior: 'JMD 280,000–550,000+' },
  ];

  return (
    <div className="space-y-5">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-xs text-yellow-800">
          <strong>Disclaimer:</strong> Salary ranges are estimates based on publicly available data and employer postings on HireMeJA. Actual pay varies by company size, location, qualifications, and experience. Figures shown are monthly gross in Jamaican Dollars (JMD).
        </p>
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-blue-950 text-white">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Industry</th>
              <th className="text-left px-4 py-3 font-semibold">Entry Level</th>
              <th className="text-left px-4 py-3 font-semibold">Mid Level</th>
              <th className="text-left px-4 py-3 font-semibold">Senior Level</th>
            </tr>
          </thead>
          <tbody>
            {salaries.map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-4 py-3 font-medium text-blue-950">{row.industry}</td>
                <td className="px-4 py-3 text-gray-600">{row.entry}</td>
                <td className="px-4 py-3 text-gray-600">{row.mid}</td>
                <td className="px-4 py-3 text-gray-600">{row.senior}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-500">Last updated: {YEAR}. Data sourced from HireMeJA job postings and publicly available wage surveys.</p>
    </div>
  );
}

function ReportsContent() {
  const reports = [
    {
      industry: 'Technology & IT',
      icon: '💻',
      summary: `Jamaica's tech sector is growing rapidly, driven by fintech, software development, and digital transformation across banking and government. Demand for developers, data analysts, and cybersecurity professionals is outpacing local supply.`,
      trend: 'High Growth',
      trendColor: 'text-green-600 bg-green-50',
      topRoles: ['Software Developer', 'IT Support Specialist', 'Data Analyst', 'Network Engineer'],
    },
    {
      industry: 'BPO & Customer Service',
      icon: '🎧',
      summary: `Jamaica remains one of the Caribbean's top BPO destinations. Major global companies operate contact centres across Kingston and Montego Bay, creating thousands of jobs annually. English proficiency and cultural affinity with North America drive demand.`,
      trend: 'Stable & Large',
      trendColor: 'text-blue-600 bg-blue-50',
      topRoles: ['Customer Service Rep', 'Team Leader', 'Quality Analyst', 'Operations Manager'],
    },
    {
      industry: 'Tourism & Hospitality',
      icon: '🏨',
      summary: `Tourism accounts for a significant portion of Jamaica's GDP. Hotels, resorts, and tour operators along the north coast are consistently hiring. Seasonal peaks around winter and summer create regular recruitment surges.`,
      trend: 'Seasonal Growth',
      trendColor: 'text-orange-600 bg-orange-50',
      topRoles: ['Front Desk Agent', 'Chef', 'Tour Guide', 'Events Coordinator'],
    },
    {
      industry: 'Healthcare',
      icon: '🏥',
      summary: `Both public and private healthcare sectors face ongoing staffing shortages. Nurses, pharmacists, and allied health professionals are in high demand. The government's expansion of community health programmes is creating new roles outside Kingston.`,
      trend: 'High Demand',
      trendColor: 'text-green-600 bg-green-50',
      topRoles: ['Registered Nurse', 'Pharmacist', 'Medical Technologist', 'Community Health Aide'],
    },
    {
      industry: 'Banking & Finance',
      icon: '🏦',
      summary: `Jamaica's financial sector — including commercial banks, insurance companies, and credit unions — offers strong career paths. Digital banking transformation is creating new demand for tech-savvy finance professionals.`,
      trend: 'Stable',
      trendColor: 'text-blue-600 bg-blue-50',
      topRoles: ['Loans Officer', 'Teller', 'Financial Analyst', 'Compliance Officer'],
    },
    {
      industry: 'Construction & Engineering',
      icon: '🏗️',
      summary: `Major infrastructure projects — including road works, housing developments, and renewable energy installations — are fuelling demand for skilled tradespeople and engineers. The government's housing programme is a significant driver.`,
      trend: 'Growing',
      trendColor: 'text-green-600 bg-green-50',
      topRoles: ['Civil Engineer', 'Project Manager', 'Electrician', 'Site Supervisor'],
    },
  ];

  return (
    <div className="space-y-5">
      <div className="bg-[#cddd3a] bg-opacity-20 border border-[#cddd3a] rounded-lg p-4">
        <p className="text-sm font-semibold text-blue-950">📊 Detailed PDF reports coming soon</p>
        <p className="text-sm text-gray-700 mt-1">Full downloadable industry reports with salary benchmarks, hiring trend data, and employer insights are in development.</p>
      </div>
      <div className="space-y-4">
        {reports.map((r, i) => (
          <div key={i} className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="flex items-start gap-3">
              <span className="text-2xl flex-shrink-0">{r.icon}</span>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-sm font-semibold text-blue-950">{r.industry}</h3>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.trendColor}`}>{r.trend}</span>
                </div>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{r.summary}</p>
                <div className="mt-3">
                  <p className="text-xs font-semibold text-gray-500 mb-1">Top roles in demand:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {r.topRoles.map(role => (
                      <span key={role} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">{role}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GetCertifiedContent() {
  const heartPrograms = [
    {
      area: 'IT & Computing',
      icon: '💻',
      courses: ['Network Technician', 'IT Support Specialist', 'Computer Systems Technology', 'Programming'],
    },
    {
      area: 'Business Administration',
      icon: '📋',
      courses: ['Office Administration', 'Customer Service', 'Accounting Technician', 'Records Management'],
    },
    {
      area: 'Tourism & Hospitality',
      icon: '🏨',
      courses: ['Food & Beverage Service', 'Housekeeping', 'Front Office Operations', 'Tour Operations'],
    },
    {
      area: 'Construction Trades',
      icon: '🏗️',
      courses: ['Carpentry & Joinery', 'Masonry & Tiling', 'Plumbing', 'Electrical Installation'],
    },
    {
      area: 'Automotive',
      icon: '🔧',
      courses: ['Motor Vehicle Technician', 'Auto Body Repair', 'Auto Electrical Systems'],
    },
    {
      area: 'Personal Services',
      icon: '✂️',
      courses: ['Cosmetology', 'Barbering', 'Nail Technology', 'Spa & Beauty Therapy'],
    },
    {
      area: 'Early Childhood',
      icon: '👶',
      courses: ['Early Childhood Care & Education (ECCE)', 'Child Development', 'Childcare & Nutrition'],
    },
    {
      area: 'Agriculture',
      icon: '🌱',
      courses: ['Crop Production', 'Animal Husbandry', 'Agricultural Mechanization'],
    },
  ];

  const molInitiatives = [
    {
      name: 'Annual National Job Fair',
      icon: '🎪',
      body: 'The Ministry of Labour hosts annual job fairs connecting thousands of Jamaican job seekers with employers from across multiple sectors. Employers conduct on-the-spot interviews and candidates can register free of charge.',
    },
    {
      name: 'National Employment Programme (NEP)',
      icon: '🤝',
      body: 'The NEP places unemployed Jamaicans in temporary work placements with public and private sector employers. It provides paid work experience, helping candidates build their CV while earning an income.',
    },
    {
      name: 'Overseas Employment (JAMERP / Farm Work)',
      icon: '✈️',
      body: 'The Ministry facilitates overseas employment through the Jamaica Migrant Workers Programme (JAMERP) and the Canadian Seasonal Agricultural Workers Programme. Eligible Jamaicans can apply for seasonal and temporary overseas placements.',
    },
    {
      name: 'Apprenticeship Programmes',
      icon: '🛠️',
      body: 'Government-backed apprenticeship schemes place young Jamaicans with registered employers for on-the-job training. Apprentices earn a wage while learning a trade or skill, combining practical experience with formal certification.',
    },
  ];

  const certGroups = [
    {
      sector: 'Finance & Accounting',
      certs: ['ACCA', 'CPA', 'CIMA', 'CFA'],
    },
    {
      sector: 'Information Technology',
      certs: ['AWS Solutions Architect', 'Microsoft Azure', 'Google Cloud (GCP)', 'CompTIA A+', 'CompTIA Security+', 'CISSP', 'ITIL 4'],
    },
    {
      sector: 'Project Management',
      certs: ['PMP', 'PRINCE2', 'Certified Scrum Master', 'PMI-ACP (Agile)'],
    },
    {
      sector: 'Human Resources',
      certs: ['SHRM-CP', 'CIPD Level 3/5', 'PHR'],
    },
    {
      sector: 'Healthcare',
      certs: ['Nursing Council of Jamaica', 'CPR / First Aid', 'Medical Coding (CPC)', 'Pharmacy Technician Certification'],
    },
  ];

  return (
    <div className="space-y-8">
      {/* HEART/NSTA Trust */}
      <section>
        <h2 className="text-xl font-bold text-blue-950 mb-1">HEART/NSTA Trust Certification Programmes</h2>
        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
          HEART/NSTA Trust is Jamaica's national training authority, offering free and subsidised vocational and technical training leading to National Vocational Qualifications of Jamaica (NVQJ). Programmes are available island-wide at HEART Academies.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          {heartPrograms.map((p, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-blue-950 flex items-center gap-2">
                <span>{p.icon}</span> {p.area}
              </p>
              <ul className="mt-2 space-y-1">
                {p.courses.map(c => (
                  <li key={c} className="text-xs text-gray-600 flex items-center gap-1.5">
                    <FaChevronRight size={8} className="text-gray-400 flex-shrink-0" /> {c}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm font-semibold text-blue-950 mb-1">How to Apply for HEART/NSTA Trust</p>
          <p className="text-sm text-gray-700 leading-relaxed">
            Visit the HEART/NSTA Trust website or your nearest HEART Academy to browse available programmes and submit an application. Most programmes require a valid ID and minimum educational qualifications. Intake periods typically run in January and September.
          </p>
        </div>
      </section>

      {/* Ministry of Labour */}
      <section>
        <h2 className="text-xl font-bold text-blue-950 mb-1">Ministry of Labour Initiatives</h2>
        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
          The Ministry of Labour &amp; Social Security runs several programmes designed to connect Jamaican workers with employment opportunities locally and overseas. HireMeJA is exploring partnership opportunities with the Ministry to support job seekers across the island.
        </p>
        <div className="space-y-4">
          {molInitiatives.map((item, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-blue-950 flex items-center gap-2">
                <span>{item.icon}</span> {item.name}
              </p>
              <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Professional Certifications */}
      <section>
        <h2 className="text-xl font-bold text-blue-950 mb-1">Professional Certifications by Sector</h2>
        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
          International professional certifications signal world-class skills and are increasingly sought by Jamaican employers — especially for senior or specialised roles. Here are the most recognised credentials by sector.
        </p>
        <div className="space-y-4">
          {certGroups.map((group, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-blue-950 mb-2">{group.sector}</p>
              <div className="flex flex-wrap gap-1.5">
                {group.certs.map(cert => (
                  <span key={cert} className="text-xs bg-blue-50 text-blue-800 border border-blue-100 px-2.5 py-1 rounded-full font-medium">
                    {cert}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <div className="bg-blue-950 text-white rounded-lg p-6 flex flex-col sm:flex-row items-center gap-4">
        <FaAward size={36} className="text-[#cddd3a] flex-shrink-0" />
        <div>
          <p className="text-base font-bold">Ready to Put Your Certification to Work?</p>
          <p className="text-sm text-blue-200 mt-1">Browse thousands of jobs across Jamaica and find employers who value your qualifications.</p>
        </div>
        <Link
          to="/jobs"
          className="sm:ml-auto flex-shrink-0 bg-[#cddd3a] text-blue-950 font-semibold text-sm px-5 py-2.5 rounded-md hover:bg-yellow-300 transition-colors"
        >
          Browse Jobs
        </Link>
      </div>
    </div>
  );
}

// ─── Sidebar ─────────────────────────────────────────────────────────────────

const Sidebar = ({ currentSlug }) => (
  <div className="space-y-6">
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-blue-950 mb-3">Career Resources</h3>
      <ul className="space-y-1.5">
        {NAV_LINKS.map(link => (
          <li key={link.slug}>
            <Link
              to={`/career-resources/${link.slug}`}
              className={`flex items-center gap-2 text-sm py-1 px-2 rounded transition-colors ${
                currentSlug === link.slug
                  ? 'bg-blue-950 text-white font-medium'
                  : 'text-gray-600 hover:text-blue-950 hover:bg-gray-50'
              }`}
            >
              <span className="text-xs">{link.icon}</span>
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>

    <div className="bg-blue-950 text-white rounded-lg p-4">
      <h3 className="text-sm font-semibold mb-2">Ready to find a job?</h3>
      <p className="text-xs text-blue-200 mb-3">Browse thousands of vacancies across Jamaica.</p>
      <Link
        to="/jobs"
        className="block text-center text-sm font-semibold bg-[#cddd3a] text-blue-950 px-4 py-2 rounded-md hover:bg-yellow-300 transition-colors"
      >
        Browse All Jobs
      </Link>
    </div>

    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-blue-950 mb-3">Browse Jobs by Industry</h3>
      <ul className="space-y-1">
        {[
          { slug: 'technology',  label: 'Technology Jobs' },
          { slug: 'healthcare',  label: 'Healthcare Jobs' },
          { slug: 'tourism',     label: 'Tourism Jobs' },
          { slug: 'banking',     label: 'Banking Jobs' },
          { slug: 'bpo',         label: 'BPO Jobs' },
        ].map(i => (
          <li key={i.slug}>
            <Link to={`/jobs/industry/${i.slug}`} className="text-sm text-gray-600 hover:text-blue-950 flex items-center gap-1">
              <FaChevronRight size={9} className="text-gray-400" /> {i.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const CareerResources = () => {
  const { slug } = useParams();
  const page = PAGES[slug];

  if (!page) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-bold text-blue-950 mb-4">Page Not Found</h1>
        <p className="text-gray-600 mb-6">This career resource page doesn't exist.</p>
        <Link to="/career-resources/blog" className="text-blue-950 hover:text-[#cddd3a] font-medium">
          Visit our Career Blog →
        </Link>
      </div>
    );
  }

  const pageUrl = `https://hiremeja.com/career-resources/${slug}`;

  return (
    <>
      <Helmet>
        <title>{page.title}</title>
        <meta name="description" content={page.description} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.description} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        {page.faqSchema && (
          <script type="application/ld+json">{JSON.stringify(page.faqSchema)}</script>
        )}
      </Helmet>

      {/* Hero */}
      <div className="bg-blue-950 text-white py-12 px-4">
        <div className="container mx-auto max-w-5xl">
          <nav className="text-sm text-blue-300 mb-4 flex items-center gap-1 flex-wrap">
            <Link to="/" className="hover:text-white">Home</Link>
            <FaChevronRight size={9} />
            <Link to="/career-resources/blog" className="hover:text-white">Career Resources</Link>
            <FaChevronRight size={9} />
            <span className="text-white">{NAV_LINKS.find(l => l.slug === slug)?.label}</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#cddd3a] uppercase">
            {page.heading}
          </h1>
          <p className="mt-3 text-blue-100 max-w-2xl text-sm leading-relaxed">
            {page.intro}
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="container mx-auto max-w-5xl px-4 py-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <main className="md:col-span-8">{page.content}</main>
          <aside className="md:col-span-4"><Sidebar currentSlug={slug} /></aside>
        </div>
      </div>
    </>
  );
};

export default CareerResources;
